import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock3, RefreshCw, ShieldAlert } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { ProviderSelect } from '@/components/llm/ProviderSelect';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { useAuth } from '@/context/AuthContext';
import { ApiError, api, type LlmResilienceAttempt, type LlmResilienceFilters } from '@/lib/api';

type ResilienceMode = 'tenant' | 'admin';

interface AdminLlmResiliencePageProps {
  mode: ResilienceMode;
}

interface LocalFilters {
  classification: string;
  routeIndex: string;
  attemptIndex: string;
  deliveryState: string;
  withBackoff: boolean;
  winnerOnly: boolean;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function formatBackoff(backoffMs: number) {
  if (backoffMs < 1_000) return `${backoffMs} ms`;
  return `${backoffMs / 1_000}s`;
}

function TimelineSkeleton() {
  return (
    <div className="space-y-3" role="status" aria-live="polite" aria-label="Carregando observabilidade…">
      <p className="text-sm text-raios-muted">Carregando observabilidade…</p>
      {[0, 1, 2].map((item) => <Skeleton key={item} className="h-20 w-full" />)}
    </div>
  );
}

function SummaryCard({ title, description, tone = 'default' }: { title: string; description: string; tone?: 'default' | 'success' | 'warning' | 'danger' }) {
  const colors = {
    default: 'border-raios-border',
    success: 'border-raios-success/40',
    warning: 'border-raios-warning/40',
    danger: 'border-raios-danger/40',
  };
  return (
    <Card className={`p-4 ${colors[tone]}`}>
      <p className="text-sm font-semibold text-raios-text">{title}</p>
      <p className="mt-1 text-xs text-raios-text-secondary">{description}</p>
    </Card>
  );
}

/**
 * Timeline de resiliência sem corpos crus, credenciais ou telemetria de custo.
 * O modo tenant nunca envia tenant_id; o modo admin só consulta com UUID explícito.
 */
export function AdminLlmResiliencePage({ mode }: AdminLlmResiliencePageProps) {
  const { tenant } = useAuth();
  const [searchParams] = useSearchParams();
  const initialAccountId = searchParams.get('account_id') ?? '';
  const authenticatedTenantId = tenant?.id ?? null;

  return (
    <AdminLlmResilienceContent
      key={`${mode}:${mode === 'tenant' ? authenticatedTenantId ?? 'anonymous' : 'admin'}`}
      mode={mode}
      tenant={tenant}
      initialAccountId={initialAccountId}
      initialTenantId={searchParams.get('tenant_id') ?? ''}
    />
  );
}

function AdminLlmResilienceContent({
  mode,
  tenant,
  initialAccountId,
  initialTenantId,
}: AdminLlmResiliencePageProps & {
  tenant: { id: string; is_admin: boolean } | null;
  initialAccountId: string;
  initialTenantId: string;
}) {
  const queryClient = useQueryClient();
  const [tenantId, setTenantId] = useState(initialTenantId);
  // per_page 100 = teto do backend (BaseFiltersSchema: .max(100)). O dono pediu
  // a listagem "paginada de 100 em 100"; 50 obrigava o dobro de cliques sem
  // ganho nenhum, já que o backend aceita 100 na mesma requisição.
  const [filters, setFilters] = useState<LlmResilienceFilters>({ account_id: initialAccountId, page: 1, per_page: 100 });
  const [localFilters, setLocalFilters] = useState<LocalFilters>({
    classification: '', routeIndex: '', attemptIndex: '', deliveryState: '', withBackoff: false, winnerOnly: false,
  });

  const isAdmin = tenant?.is_admin === true;
  const authenticatedTenantId = tenant?.id ?? null;
  const adminTenantReady = UUID_RE.test(tenantId);
  const queryTenantId = mode === 'tenant' ? authenticatedTenantId : (tenantId || null);
  const canLoad = mode === 'tenant'
    ? authenticatedTenantId !== null
    : isAdmin && adminTenantReady;

  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: ['llm-resilience', 'attempts', mode, queryTenantId] });
      queryClient.removeQueries({ queryKey: ['llm-resilience', 'summary', mode, queryTenantId] });
    };
  }, [mode, queryClient, queryTenantId]);

  const queryKey = [
    'llm-resilience', 'attempts', mode, queryTenantId, filters.logical_call_id, filters.agent_execution_id,
    filters.status, filters.provider, filters.model_key, filters.account_id, filters.page, filters.per_page,
  ];
  const attemptsQuery = useQuery({
    queryKey,
    enabled: canLoad,
    queryFn: () => mode === 'admin'
      ? api.getAdminLlmResilience({ ...filters, tenant_id: tenantId })
      : api.getLlmResilienceAttempts(filters),
  });
  const summaryQuery = useQuery({
    queryKey: ['llm-resilience', 'summary', mode, queryTenantId],
    enabled: mode === 'tenant' && authenticatedTenantId !== null,
    queryFn: () => api.getLlmResilienceSummary(),
  });

  const attempts = attemptsQuery.data;
  const summary = summaryQuery.data;
  const logicalCalls = attempts?.logical_calls ?? [];
  const winningCallIds = useMemo(
    () => new Set((attempts?.logical_calls ?? []).filter((item) => item.winning_route).map((item) => item.logical_call_id)),
    [attempts?.logical_calls],
  );
  const displayedAttempts = useMemo(() => (attempts?.items ?? []).filter((attempt) => {
    if (localFilters.classification && attempt.classification !== localFilters.classification) return false;
    if (localFilters.routeIndex && attempt.route_index !== Number(localFilters.routeIndex)) return false;
    if (localFilters.attemptIndex && attempt.attempt_index !== Number(localFilters.attemptIndex)) return false;
    if (localFilters.deliveryState && attempt.delivery_state !== localFilters.deliveryState) return false;
    if (localFilters.withBackoff && attempt.backoff_ms <= 0) return false;
    if (localFilters.winnerOnly && !winningCallIds.has(attempt.logical_call_id)) return false;
    return true;
  }), [attempts?.items, localFilters, winningCallIds]);

  const hasPartialData = summary?.partial === true || (attempts?.logical_calls ?? []).some(
    (call) => call.observability_degraded || call.metadata_consistent === false,
  );
  const hasExhaustion = (summary?.totals.exhausted_calls ?? 0) > 0 || (attempts?.logical_calls ?? []).some(
    (call) => call.delivery_state === 'exhausted',
  );
  const hasInProgress = displayedAttempts.some((attempt) => attempt.outcome === 'started' || attempt.outcome === 'advanced')
    || (attempts?.logical_calls ?? []).some((call) => call.delivery_state === 'in_progress');
  const winningRoute = (attempts?.logical_calls ?? []).find((call) => call.winning_route)?.winning_route;
  const hasSuccess = (summary?.totals.successful_calls ?? 0) > 0 || Boolean(winningRoute)
    || displayedAttempts.some((attempt) => attempt.outcome === 'succeeded');
  const forbidden = attemptsQuery.error instanceof ApiError
    && (attemptsQuery.error.status === 403 || attemptsQuery.error.code === 'FORBIDDEN');

  const setRemoteFilter = <K extends keyof LlmResilienceFilters>(key: K, value: LlmResilienceFilters[K]) => {
    setFilters((current) => ({ ...current, [key]: value || undefined, page: 1 }));
  };
  const setLocalFilter = <K extends keyof LocalFilters>(key: K, value: LocalFilters[K]) => {
    setLocalFilters((current) => ({ ...current, [key]: value }));
  };
  const setAdminTenant = (value: string) => {
    setTenantId(value);
    setFilters((current) => current.page === 1 ? current : { ...current, page: 1 });
  };
  const setPage = (page: number) => setFilters((current) => ({ ...current, page }));

  if (mode === 'admin' && !isAdmin) {
    return (
      <div className="p-4 md:p-6">
        <PageHeader title="Resiliência LLM" description="Linha do tempo operacional de tentativas e fallback." />
        <Card className="mt-6 max-w-xl border-raios-warning/40 p-5">
          <ShieldAlert className="size-5 text-raios-warning" aria-hidden="true" />
          <h2 className="mt-3 text-base font-semibold text-raios-text">Acesso restrito</h2>
          <p className="mt-1 text-sm text-raios-text-secondary">Esta visão administrativa exige a permissão de administrador do tenant.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <PageHeader
        title="Resiliência LLM"
        description={mode === 'admin'
          ? 'Investigação administrativa por tenant, rota e estado de entrega.'
          : 'Entenda o fallback das suas contas sem depender de logs crus.'}
        actions={<Button variant="outline" onClick={() => { void attemptsQuery.refetch(); if (mode === 'tenant') void summaryQuery.refetch(); }}><RefreshCw className="size-4" aria-hidden="true" />Atualizar</Button>}
      />

      {mode === 'admin' && (
        <Card className="p-4">
          <Input label="Tenant ID" value={tenantId} onChange={(event) => setAdminTenant(event.target.value)} placeholder="UUID do tenant" />
          {!adminTenantReady && <p className="mt-2 text-xs text-raios-muted">Informe um UUID de tenant para consultar a timeline administrativa.</p>}
        </Card>
      )}

      <Card className="p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <ProviderSelect label="Provider" value={filters.provider ?? ''} onChange={(value) => setRemoteFilter('provider', value)} />
          <Input label="Modelo" value={filters.model_key ?? ''} onChange={(event) => setRemoteFilter('model_key', event.target.value)} placeholder="model_key" />
          <Input label="Conta" value={filters.account_id ?? ''} onChange={(event) => setRemoteFilter('account_id', event.target.value)} placeholder="account_id" />
          <Select
            label="Status"
            value={filters.status ?? ''}
            onChange={(value) => setRemoteFilter('status', value as LlmResilienceAttempt['outcome'])}
            options={[
              { value: '', label: 'Todos os status' }, { value: 'started', label: 'Iniciado' },
              { value: 'advanced', label: 'Fallback em curso' }, { value: 'succeeded', label: 'Sucesso' }, { value: 'failed', label: 'Falhou' },
            ]}
          />
          <Input label="Classificação" value={localFilters.classification} onChange={(event) => setLocalFilter('classification', event.target.value)} placeholder="Ex.: advance_account" />
          <Input label="Índice da rota" type="number" min="0" value={localFilters.routeIndex} onChange={(event) => setLocalFilter('routeIndex', event.target.value)} />
          <Input label="Índice da tentativa" type="number" min="0" value={localFilters.attemptIndex} onChange={(event) => setLocalFilter('attemptIndex', event.target.value)} />
          <Input label="Estado de entrega" value={localFilters.deliveryState} onChange={(event) => setLocalFilter('deliveryState', event.target.value)} placeholder="Ex.: materialized" />
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-sm text-raios-text-secondary">
          <label className="flex items-center gap-2"><input type="checkbox" checked={localFilters.withBackoff} onChange={(event) => setLocalFilter('withBackoff', event.target.checked)} />Com backoff</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={localFilters.winnerOnly} onChange={(event) => setLocalFilter('winnerOnly', event.target.checked)} />Apenas chamadas com rota vencedora</label>
        </div>
      </Card>

      {forbidden ? (
        <Card className="border-raios-warning/40 p-5">
          <ShieldAlert className="size-5 text-raios-warning" aria-hidden="true" />
          <h2 className="mt-3 text-base font-semibold text-raios-text">Acesso restrito</h2>
          <p className="mt-1 text-sm text-raios-text-secondary">A consulta foi recusada antes de expor dados de outro tenant.</p>
        </Card>
      ) : attemptsQuery.isError ? (
        <Alert variant="destructive"><div><AlertTitle>Não foi possível carregar a observabilidade.</AlertTitle><AlertDescription>Tente atualizar; nenhum conteúdo sensível é exibido nesta tela.</AlertDescription></div></Alert>
      ) : !canLoad ? null : attemptsQuery.isLoading || (mode === 'tenant' && summaryQuery.isLoading) ? <TimelineSkeleton /> : (
        <>
          {hasPartialData && <Alert variant="warning"><div><AlertTitle>Dados parciais</AlertTitle><AlertDescription>A agregação foi limitada ou parte da telemetria não está consistente. Use esta timeline como orientação operacional.</AlertDescription></div></Alert>}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <SummaryCard title="Fallback aplicado" description={`${summary?.totals.fallback_calls ?? (attempts?.logical_calls ?? []).filter((call) => call.fallback_depth > 0).length} chamadas avançaram para uma rota alternativa.`} tone="warning" />
            {hasSuccess && <SummaryCard title="Rota vencedora" description={winningRoute ? `${winningRoute.provider} / ${winningRoute.model_key}` : 'Uma tentativa concluiu a entrega com sucesso.'} tone="success" />}
            {hasInProgress && <SummaryCard title="Fallback em curso" description="A cadeia ainda está avançando; aguarde a conclusão antes de repetir a operação." tone="warning" />}
            {hasExhaustion && <SummaryCard title="Rotas esgotadas" description="Nenhuma rota elegível concluiu esta chamada. Revise a elegibilidade e a policy do agente." tone="danger" />}
          </div>
          {logicalCalls.length > 0 && (
            <Card className="p-4">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
                <h2 id="fallback-depth-heading" className="text-sm font-semibold text-raios-text">Profundidade de fallback por chamada</h2>
                <p className="text-xs text-raios-text-secondary">Cada tentativa abaixo identifica a chamada lógica correspondente.</p>
              </div>
              <ul aria-labelledby="fallback-depth-heading" className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {logicalCalls.map((call) => (
                  <li
                    key={call.logical_call_id}
                    aria-label={`Chamada ${call.logical_call_id}: profundidade de fallback ${call.fallback_depth}`}
                    className="flex items-center justify-between gap-3 rounded-md border border-raios-border px-3 py-2"
                  >
                    <span className="min-w-0 truncate font-mono text-xs text-raios-text-secondary">Chamada {call.logical_call_id}</span>
                    <Badge variant={call.fallback_depth > 0 ? 'warning' : 'default'} className="shrink-0">Fallback {call.fallback_depth}</Badge>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          {displayedAttempts.length === 0 ? (
            <EmptyState icon={Clock3} title="Nenhuma tentativa encontrada" description="Ajuste os filtros ou execute uma chamada para iniciar a observabilidade." />
          ) : (
            <Card className="overflow-x-auto p-0">
              <table className="w-full min-w-[760px] text-left text-sm">
                <caption className="sr-only">Tentativas de resiliência LLM</caption>
                <thead className="border-b border-raios-border text-xs uppercase tracking-wide text-raios-muted">
                  <tr><th scope="col" className="px-4 py-3">Rota</th><th scope="col" className="px-4 py-3">Resultado</th><th scope="col" className="px-4 py-3">Classificação</th><th scope="col" className="px-4 py-3">Backoff</th><th scope="col" className="px-4 py-3">Entrega</th></tr>
                </thead>
                <tbody>
                  {displayedAttempts.map((attempt) => <AttemptRow key={attempt.id} attempt={attempt} winner={winningCallIds.has(attempt.logical_call_id)} />)}
                </tbody>
              </table>
            </Card>
          )}
          {attempts?.pagination && (
            <div className="flex items-center justify-between gap-3" aria-label="Paginação das tentativas">
              <p className="text-sm text-raios-text-secondary tabular-nums">Página {attempts.pagination.page} de {attempts.pagination.total_pages}</p>
              <div className="flex gap-2">
                <Button variant="outline" aria-label="Página anterior" disabled={attempts.pagination.page <= 1} onClick={() => setPage(attempts.pagination.page - 1)}>Anterior</Button>
                <Button variant="outline" aria-label="Próxima página" disabled={!attempts.pagination.has_next} onClick={() => setPage(attempts.pagination.page + 1)}>Próxima</Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AttemptRow({ attempt, winner }: { attempt: LlmResilienceAttempt; winner: boolean }) {
  const statusVariant = attempt.outcome === 'succeeded' ? 'success' : attempt.outcome === 'failed' ? 'danger' : 'warning';
  return (
    <tr className="border-b border-raios-border last:border-0">
      <td className="px-4 py-3"><p className="font-medium text-raios-text">{attempt.provider} / {attempt.model_key}</p><p className="text-xs text-raios-muted">chamada {attempt.logical_call_id}</p><p className="text-xs text-raios-muted">rota {attempt.route_index} · tentativa {attempt.attempt_index}{winner ? ' · vencedora' : ''}</p></td>
      <td className="px-4 py-3"><Badge variant={statusVariant}>{attempt.outcome}</Badge>{attempt.error_code && <p className="mt-1 text-xs text-raios-muted">{attempt.error_code}</p>}</td>
      <td className="px-4 py-3 text-raios-text-secondary">{attempt.classification}</td>
      <td className="px-4 py-3 text-raios-text-secondary">{attempt.backoff_ms > 0 ? `Backoff ${formatBackoff(attempt.backoff_ms)}` : '—'}</td>
      <td className="px-4 py-3 text-raios-text-secondary">{attempt.delivery_state ?? '—'}</td>
    </tr>
  );
}
